"use client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import Image from "next/image";
import Slope from "@/components/ui/Slope";
import Icon from "@/components/icons";
import CourseVideoDialog from "@/components/CourseVideoDialog";
import Launch from "@/components/icons/Launch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PartialPartner } from "@/common/partner/types";
import Link from "next/link";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import { Video } from "@/common/course/types";
import { addQueryParamsToUrl } from "@/lib/course/utils/addQueryParamsToUrl";
import CTALink from "@/components/CTA/ctaLink";

export type ExecEdProps = {
  title?: string;
  partners?: PartialPartner[];
  subheader?: string;
  path?: string;
  courseImage?: string;
  video?: Video;
  isOrganizationLink?: boolean;
  courseDisclaimer?: string;
  isSprint: boolean;
  organicUrl?: string | null;
  externalIdentifier?: string | null;
  subject?: string;
};

export default function ExecEdHeader({
  title,
  partners,
  subheader,
  path,
  courseImage,
  video,
  isOrganizationLink,
  courseDisclaimer = "course",
  isSprint,
  organicUrl,
  externalIdentifier,
  subject,
}: ExecEdProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  function getOrganizationLogo(partner: PartialPartner) {
    const logo = (
      <Image
        width={90}
        height={48}
        src={partner.logoUrl}
        alt={partner.name || "Organization Logo"}
        key={partner.key}
      />
    );
    if (isOrganizationLink) {
      return (
        <Link
          className="mr-2 mb-0"
          href={`/school/${partner.slug.toLocaleLowerCase()}`}
          key={partner.key}
        >
          {logo}
        </Link>
      );
    }
    return logo;
  }

  function getEnrollButton() {
    const cartUrl = externalIdentifier
      ? `https://getsmarter.com/titan/api/v1/cart/add_item/${externalIdentifier}`
      : null;

    if (!cartUrl) {
      return null;
    }

    let enrollButtonUrl = addQueryParamsToUrl({
      url: cartUrl,
      includeUtmCampaign: false,
      organicUrl,
      extraQueryParams: {
        external_link: true,
      },
    });

    return (
      <div className="flex flex-col w-full md:w-auto">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <a
              className="bg-brand text-sm md:text-base font-semibold w-[11rem] min-h-11 md:w-auto hover:bg-white hover:text-brand text-white border-2 border-brand rounded-md"
              onMouseEnter={() => setIsPopoverOpen(true)}
              onMouseLeave={() => setIsPopoverOpen(false)}
              href={enrollButtonUrl}
              style={{ outline: "none" }}
              target="_blank"
            >
              <div className="flex flex-row py-2 px-4 justify-center">
                <span className="pr-1">Enroll now</span>
                <Launch svgProps={{ width: "24", height: "24" }} />
              </div>
            </a>
          </PopoverTrigger>
          <PopoverContent className="bg-primary p-4 rounded-xl max-w-56 mx-auto text-white text-sm text-center">
            You will be taken to GetSmarter in the current tab.
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden md:mb-[-48px] lg:mb-[-38px] sm:mb-2 bg-light-200 sm:pt-12 md:pt-7 ${!isSprint ? "pt-10" : ""}`}
    >
      <div className="container mx-auto relative">
        <div className="pt-3 text-light-foreground">
          <Breadcrumbs
            variant="dark"
            path={path || ""}
            linksToShowOnSmallScreen={1}
            title={title}
            isExecEd
          />
        </div>

        <div className="flex flex-col md:flex-row justify-between">
          <div className="max-w-full md:max-w-[60%] flex-grow pb-0 md:pb-[48px] lg:pb-[38px]">
            <div className="mt-4 mb-2 flex flex-row space-x-2">
              {partners &&
                partners.map((partner) => getOrganizationLogo(partner))}
            </div>
            <h1 className="text-3xl font-bold mt-2 mb-3 lg:mb-4 p-0">
              {title}
            </h1>

            <div className="mb-4 hidden md:block">
              <p>{subheader}</p>
            </div>
            <div className="mb-4 block md:hidden">
              <ExpandableBlock maxHeight={60} heading="" blur>
                <p className="prose" style={{ wordBreak: "keep-all" }}>
                  {subheader}
                </p>
              </ExpandableBlock>
            </div>

            <div className="mb-6">
              <span className="text-xs text-gray-500">
                This {courseDisclaimer} is offered by&nbsp;
                <a href="#whygetsmarter" className="underline">
                  GetSmarter
                </a>
                , an edX partner.
              </span>
            </div>

            <div className="flex flex-row mb-10 mt-3 md:mt-0 justify-center md:justify-start gap-2 md:gap-5">
              {isSprint && getEnrollButton()}

              <div className="flex flex-col w-full md:w-auto">
                <CTALink
                  label="Request more info"
                  url="#brochure"
                  type="brand"
                  enable_tracking={true}
                  className="text-sm md:text-base font-semibold px-4 py-2 w-[11rem] min-h-11 md:w-auto"
                  location="product-header"
                  html_id="request-info-CTA"
                  name={subject}
                />
              </div>
            </div>
          </div>

          <div className="hidden md:flex shrink-1 relative basis-[406px] md:mr-[-92px] lg:basis-[438px] lg:mr-[-15px] xl:basis-[538px] xl:mr-[-15px]">
            <Image
              src={courseImage || ""}
              alt={title || ""}
              layout="responsive"
              width={800}
              height={450}
              className="w-full h-auto object-cover"
            />
            <Slope className="absolute top-0 left-0 w-[78px] h-full border-l text-white border-white -ml-[2px]" />

            {video && video.src && (
              <>
                <Button
                  onClick={() => setOpen(true)}
                  variant="inversePrimary"
                  className="bg-white text-base hover:bg-primary hover:border-transparent hover:text-white px-4 py-2"
                  radius="md"
                >
                  <span className="mr-2 -ml-1">
                    <Icon type="circle-play" />
                  </span>
                  Play Video
                </Button>
                <CourseVideoDialog
                  isOpen={open}
                  close={() => setOpen(false)}
                  title={title || ""}
                  videoUrl={video.src}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
